import { Controller } from '@hotwired/stimulus'
import { prefersReducedMotion } from './mixins/reduced_motion'

const SPEED = 26

export default class extends Controller {
  static targets = ['track', 'group']
  static values = { direction: { type: Number, default: 1 } }

  connect () {
    this.paused = false
    this.dragging = false
    this.position = 0

    this.onResize = () => this.scheduleFill()
    window.addEventListener('resize', this.onResize)

    this.observer = new IntersectionObserver(
      entries => {
        if (!entries[0].isIntersecting) return this.stop()
        this.fill()
        this.start()
      },
      { threshold: 0 }
    )
    this.observer.observe(this.element)
  }

  disconnect () {
    this.stop()
    this.observer?.disconnect()
    window.removeEventListener('resize', this.onResize)
    if (this.fillFrame) cancelAnimationFrame(this.fillFrame)
  }

  pause (event) {
    if (event.pointerType !== 'mouse') return
    this.paused = true
  }

  resume (event) {
    if (event.pointerType !== 'mouse') return
    this.paused = false
  }

  startDrag (event) {
    if (event.pointerType !== 'mouse') return
    this.dragging = true
    this.originX = event.clientX
    this.originScroll = this.element.scrollLeft
    this.element.setPointerCapture(event.pointerId)
    this.element.classList.add('cursor-grabbing')
  }

  drag (event) {
    if (!this.dragging) return
    event.preventDefault()
    this.element.scrollLeft = this.originScroll - (event.clientX - this.originX)
  }

  endDrag (event) {
    if (!this.dragging) return
    this.dragging = false
    this.position = this.element.scrollLeft
    this.element.releasePointerCapture(event.pointerId)
    this.element.classList.remove('cursor-grabbing')
  }

  // Private

  scheduleFill () {
    if (this.fillFrame) return
    this.fillFrame = requestAnimationFrame(() => {
      this.fillFrame = null
      this.fill()
    })
  }

  fill () {
    const span = this.groupTarget.getBoundingClientRect().width
    if (span <= 0) return
    this.span = span

    const wanted = Math.ceil(this.element.clientWidth / span) + 1
    while (this.trackTarget.children.length < wanted) {
      const clone = this.groupTarget.cloneNode(true)
      clone.removeAttribute('data-companion-ticker-target')
      clone.setAttribute('aria-hidden', 'true')
      this.trackTarget.appendChild(clone)
    }
  }

  start () {
    if (this.frame || prefersReducedMotion()) return
    this.last = performance.now()
    this.frame = requestAnimationFrame(this.tick)
  }

  stop () {
    if (!this.frame) return
    cancelAnimationFrame(this.frame)
    this.frame = null
  }

  tick = timestamp => {
    const elapsed = Math.min((timestamp - this.last) / 1000, 0.05)
    this.last = timestamp

    if (this.span && !this.paused && !this.dragging) {
      const next = this.position + this.directionValue * SPEED * elapsed
      this.position = ((next % this.span) + this.span) % this.span
      this.element.scrollLeft = this.position
    }
    this.frame = requestAnimationFrame(this.tick)
  }
}
